import React, { useEffect, useMemo, useState } from 'react';
import { Card, Pagination, Form } from 'react-bootstrap';
import ApiService from 'helpers/apiService';
import RatesTable from './RatesTable';
import { IRateTypes, IProvider } from './types';

const PAGE_SIZE = 50;

type FilterStatus = 'all' | 'active' | 'inactive';

const Rates = () => {
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState<IRateTypes[]>([]);
  const [providers, setProviders] = useState<IProvider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterPairBase, setFilterPairBase] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);


  const getData = async (provider_id: number) => {
    setLoading(true);
    try {
      const response = await ApiService.getRates<{ data: IRateTypes[] }>(provider_id);
      setRates(response.data || []);
    } catch (error) {
      console.error('Ошибка загрузки торговых пар:', error);
      setRates([]);
    } finally {
      setLoading(false);
    }
  };

  const getDataAllProviders = async () => {
    if (!providers.length) return;
    setLoading(true);
    try {
      const results = await Promise.all(
        providers.map(p => ApiService.getRates<{ data: IRateTypes[] }>(p.id).then(r => r.data || []))
      );
      setRates(results.flat());
    } catch (error) {
      console.error('Ошибка загрузки торговых пар:', error);
      setRates([]);
    } finally {
      setLoading(false);
    }
  };
 // Загрузка списка провайдеров
  const getProviders = async () => {
    try {
      const response = await ApiService.getProviders<{ data: IProvider[] }>({});
      if (response.data?.length) {
        setProviders(response.data);
        setSelectedProviderId(response.data[0]?.id);
      }
    } catch (error) {
      console.error('Ошибка загрузки провайдеров:', error);
    }
  };

  const handleRateUpdate = (updatedRate: IRateTypes) => {
    setRates(prev =>
      prev.map(rate => (rate.id === updatedRate.id ? updatedRate : rate))
    );
  };

  useEffect(() => {
    getProviders();
  }, []);

  // При статусе «Все» или «Неактивные» сбрасывать провайдера на одного из списка
  useEffect(() => {
    if (filterStatus !== 'active' && selectedProviderId === null && providers.length > 0) {
      setSelectedProviderId(providers[0].id);
    }
  }, [filterStatus, selectedProviderId, providers]);

  useEffect(() => {
    if (selectedProviderId !== null) {
      getData(selectedProviderId);
    } else if (filterStatus === 'active' && providers.length > 0) {
      getDataAllProviders();
    }
  }, [selectedProviderId, filterStatus, providers.length]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterPairBase, selectedProviderId]);

  const filteredRates = useMemo(() => {
    let list = rates;
    if (filterStatus === 'active') list = list.filter(r => r.enable_history);
    else if (filterStatus === 'inactive') list = list.filter(r => !r.enable_history);
    if (filterPairBase.trim()) {
      const base = filterPairBase.trim().toUpperCase();
      list = list.filter(r => {
        const first = (r.symbol || '').split('/')[0].trim().toUpperCase();
        return first === base || first.startsWith(base);
      });
    }
    return list;
  }, [rates, filterStatus, filterPairBase]);

  const totalPages = Math.max(1, Math.ceil(filteredRates.length / PAGE_SIZE));
  const paginatedRates = useMemo(
    () => filteredRates.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredRates, currentPage]
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <Card>
      <Card.Body>
        <div className="mb-4 d-flex flex-wrap align-items-end gap-3">
          <div>
            <Form.Label>Выберите провайдера</Form.Label>
            <Form.Select
              value={selectedProviderId ?? ''}
              onChange={(e) => setSelectedProviderId(e.target.value === '' ? null : Number(e.target.value))}
              style={{ minWidth: 200 }}
            >
              {filterStatus === 'active' && <option value="">Все</option>}
              {providers.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Form.Select>
          </div>
          <div>
            <Form.Label>Статус</Form.Label>
            <Form.Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
              style={{ minWidth: 160 }}
            >
              <option value="all">Все</option>
              <option value="active">Активные</option>
              <option value="inactive">Неактивные</option>
            </Form.Select>
          </div>
          <div>
            <Form.Label>Пара (первое значение)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Напр. BTC, ETH"
              value={filterPairBase}
              onChange={(e) => setFilterPairBase(e.target.value)}
              style={{ minWidth: 140 }}
            />
          </div>
        </div>
        {loading ? (
          <div>Загрузка...</div>
        ) : (
          <>
            <div className="mb-2 text-muted small">
              Показано {filteredRates.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredRates.length)} из {filteredRates.length}
            </div>
            <RatesTable
              data={paginatedRates}
              providers={providers}
              onUpdate={handleRateUpdate}
            />
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <Pagination className="mb-0">
                  <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                  <Pagination.Item active>{currentPage}</Pagination.Item>
                  <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages} />
                  <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage >= totalPages} />
                </Pagination>
                <span className="text-muted">Страница {currentPage} из {totalPages}</span>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default Rates;